//@flow
import React from 'react';
import QuoteTotalsModel from '../../Stores/Quote/quoteTotalsModel';
import QuoteModel from '../../Stores/Quote/quoteModel';
import MembershipPlanModel from '../../Stores/Membership/membershipPlanModel';
import ShippingInfo from '../../Utils/shippingInfoUtil';
import TotalItem from './TotalItem.react';
import MsrpToggleInner from './MsrpToggleInner.react';
import SubscriptionSwitcher from '../../Checkout/components/SubscriptionSwitcher.react';
import { isDiscountNotApplicable as isS2SNotApplicable, getSubDiscountName } from '../../Utils/sub2save';
import { IfExperiment, IfNotExperiment } from '../../Experiments/components/ExperimentWrapper.react';

type Props = {
  inCheckout?:boolean,
  quoteTotals:QuoteTotalsModel,
  newMember:boolean,
  loggedIn?:boolean,
  inCartPreview?:boolean,
  quote:QuoteModel,
  membershipPlan:MembershipPlanModel,
  showSub2Save?:boolean,
  moreMsrpToggleText?:boolean,
  hasSubscription:boolean,
  showMsrpRow:boolean,
  hasSubscriptionBox?:boolean,
  hideMembershipOption?:boolean,
};

export default function PriceSummary(props:Props) {
  if (!props.quoteTotals.getSubtotal()) return null;

  if (isS2SNotApplicable(props.quote, props.quoteTotals)) {
    return (
      <div className="price-summary v2">
        <div className="text-2 price-summary-items">
          {_getMemberSubtotalRawComponent(props)}
          <TotalItem
            itemName="Discount"
            itemClassName="row accent-1 bold"
            total={props.quoteTotals.getDiscount()}
            showIfNull={false}
            isNegative={true}
          />
          {_getShippingComponent(props)}
          {_getTaxComponent(props)}
          {_getStoreCreditComponent(props)}
        </div>
        {_getGrandTotalComponent(props)}
      </div>
    );
  }

  return (
    <div className="price-summary v2">
      <div className="text-2 price-summary-items">
        {_getSubtotalComponent(props)}
        {_getMsrpToggle(props)}
        <IfNotExperiment name="condensed_checkout_summary">
          {_getMemberDiscountComponent(props)}
          {_getDiscountDetailsComponent(props)}
        </IfNotExperiment>
        <IfExperiment name="condensed_checkout_summary">
          {_getCombinedDiscountComponent(props)}
        </IfExperiment>
        {_getSubscriptionSwitcher(props)}
        {_getTotalDiscountComponent(props)}
        <IfNotExperiment name="condensed_checkout_summary">
          {_getMemberSubtotalComponent(props)}
        </IfNotExperiment>
        {_getShippingComponent(props)}
        {_getTaxComponent(props)}
        {_getStoreCreditComponent(props)}
      </div>
      {_getGrandTotalComponent(props)}
    </div>
  );
}

//Sub switcher is rendered in checkout as well
function _getSubscriptionSwitcher(props:Props) {
  if (!props.showSub2Save || !props.inCheckout) return null;

  return (
    <SubscriptionSwitcher
      quote={props.quote}
      quoteTotals={props.quoteTotals}
      hasSubscription={props.hasSubscription}
    />
  );
}

function _getMsrpToggle(props:Props) {
  const memberSavings = props.quoteTotals.getMsrpMemberSavings() || 0;
  if (!props.newMember || !memberSavings || props.membershipPlan.hideMembershipInfo() || props.hideMembershipOption) return null;

  return (
    <MsrpToggleInner
      membershipDiscount={memberSavings}
      quoteId={props.quote.getId()}
      isMsrp={props.quote.isMsrp()}
      moreText={props.moreMsrpToggleText}
      membershipPlan={props.membershipPlan}
      showRow={props.showMsrpRow}
    />
  );
}

function _getSubtotalComponent(props:Props, total = null) {
  if (props.membershipPlan.hideMembershipInfo()) return null;

  let itemName = "Starting subtotal";
  return (
    <div>
      <IfExperiment name="condensed_checkout_summary" hide={true}>
        {itemName = "Subtotal"}
      </IfExperiment>
      <TotalItem
        itemName={itemName}
        total={total || props.quoteTotals.getRegularSubtotal()}
        showIfNull={false}
      />
    </div>
  );
}

function _getCombinedDiscountComponent(props:Props) {
  return (
    <TotalItem
      itemName="Discount"
      itemClassName="row accent-1 bold member-discount"
      total={props.quoteTotals.getSavings()}
      showIfNull={false}
      isNegative={true}
    />
  );
}

function _getMemberDiscountComponent(props:Props) {
  // don't show member discount for new customers
  if (props.newMember || props.membershipPlan.hideMembershipInfo()) return null;
  if (props.hasSubscriptionBox) return null;

  return (
    <TotalItem
      itemName="Member discount"
      itemClassName="row accent-1 bold member-discount"
      total={props.quoteTotals.getMemberSavings()}
      showIfNull={false}
      isNegative={true}
    />
  );
}

function _getMemberSubtotalComponent(props:Props) {
  const text = "Subtotal";

  const subtotalLessDiscounts = props.quoteTotals.getSubtotalLessDiscounts();

  return (
    <TotalItem
      itemName={text}
      itemClassName="row bold"
      total={subtotalLessDiscounts}
    />
  );
}

function _getMemberSubtotalRawComponent(props:Props) {
  const text = "Price";
  const subtotalLessDiscounts = props.quoteTotals.getMemberSubtotal();

  return (
    <TotalItem
      itemName={text}
      itemClassName="row bold"
      total={subtotalLessDiscounts}
    />
  );
}

function _getTotalDiscountComponent(props:Props) {
  if (!props.hasSubscriptionBox) return;
  const regularSubtotal = props.quoteTotals.getRegularSubtotal() || 0;
  const subtotal  = props.quoteTotals.getSubtotalLessDiscounts() || 0;
  const discount = regularSubtotal - subtotal;
  return (
    <TotalItem
      itemName="Discount"
      itemClassName="row accent-1 bold"
      total={discount}
      showIfNull={false}
      isNegative={true}
    />
  );
}

function _getDiscountDetailsComponent(props:Props) {
  const discounts = props.quoteTotals && props.quoteTotals.getDiscountDetails();
  if (!discounts || props.hasSubscriptionBox) return;
  //sort by the coupon type here so that auto applied discounts, such as subscribeToSave,
  //appear last.
  return discounts.sort((a,b) => b.coupon_type - a.coupon_type).map(discount => {
    let itemName = discount.name;
    //coupon_type can be 1 (subscribe and save discount) or 2 (everything else)
    if (discount.coupon_type === 2) {
      const discountName = (discount.name && ` (${discount.name})`) || '';
      itemName = `Promotion${discountName}`;
    }

    if ((props.showSub2Save || props.hasSubscriptionBox) && discount.name === getSubDiscountName()) return null;

    return (
      <TotalItem
        key={discount.name}
        itemName={itemName}
        itemClassName="row accent-1 bold"
        total={discount.value}
        showIfNull={false}
        isNegative={true}
      />
    );
  });
}

function _getStoreCreditComponent(props:Props) {
  return (
    <TotalItem
      itemName="Store credit"
      total={props.quoteTotals.getCustomerBalance()}
      showIfNull={false}
      isNegative={true}
    />
  );
}

function _getShippingComponent(props:Props) {
  return (
    <TotalItem
      itemName={_getShippingTime(props)}
      total={_getShippingAmount(props)}
    />
  );
}

function _getTaxComponent(props:Props) {
  const tax = props.quoteTotals && props.quoteTotals.getTax();
  if (!tax) return;

  return (
    <TotalItem
      itemName="Tax"
      total={tax}
      showIfNull={false}
      isNegative={false}
    />
  );
}

function _getGrandTotalComponent(props:Props) {
  const name = props.inCheckout ? 'Order total' : 'Estimated total:';

  return (
    <TotalItem
      itemClassName="text-2 row bold grand-total-item"
      itemName={name}
      total={props.quoteTotals.getGrandTotal()}
      itemNameClassName="columns small-8"
      itemTotalClassName="columns small-4 grand-total"
    />
  );
}

function _getShippingAmount(props:Props) {
  const shipping = props.quoteTotals.getShipping() || 0;
  if (shipping === 0) return 'FREE';
  return shipping;
}

function _getShippingTime(props:Props):string {
  const shippingRate = props.quote && props.quote.getShippingRateId();
  if (!shippingRate) return 'Shipping(5-7 business days)';
  const values = ShippingInfo.SHIPPING_VALUES[shippingRate];
  if (values) return `${values.name} ${values.time}`;
  return 'Shipping(5-7 business days)';
}
